import { FC, FormEvent, PropsWithChildren, useEffect, useRef, useState } from 'react';

import { useGetPolicySettingsQuery } from '@entities/policy';
import {
  IEditorRule,
  IEditorRuleInput,
  RULE_LIMIT_MAX,
  RULE_LIMIT_MIN,
  validateRule,
} from '@entities/routing/config-version';
import { Stack, Typography } from '@mui/material';
import { FormItem } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';

import { TRuleParam, toRuleParamInputs, toRuleParams } from './ruleParamInputs';
import RuleParamsSection from './RuleParamsSection';
import { toOptions } from './selectOptions';

interface IProps {
  rule?: Pick<
    IEditorRule,
    'name' | 'description' | 'min' | 'max' | 'conditions' | 'selections' | 'sort_strategy'
  >;
  onSubmit: (input: IEditorRuleInput) => void;
}

type TLimit = 'min' | 'max';
type TField = 'name' | TLimit;
type TFieldErrors = Partial<Record<TField, string>>;

const LIMITS: { key: TLimit; label: string }[] = [
  { key: 'min', label: 'Min Amount' },
  { key: 'max', label: 'Max Amount' },
];

interface IFieldProps {
  id: string;
  label: string;
  error?: string;
}

// FormItemWithError markup with a local (not RTK/Zod) error.
const Field: FC<PropsWithChildren<IFieldProps>> = ({ id, label, error, children }) => (
  <div className={`form-item ${error ? 'has-error' : ''}`}>
    <label htmlFor={id}>{label}</label>
    <div className='form-field'>{children}</div>
    <div className='form-error' role={error ? 'alert' : undefined}>
      {error}
    </div>
  </div>
);

// An empty limit is not 0: the user must type it.
const toLimit = (value: string) => (value.trim() === '' ? NaN : Number(value));
// A new rule (or a rule without a stored limit) starts with empty fields, not 0 — 0 is
// below the allowed range, so prefilling it would only hand the user an error.
const toLimitInput = (value: number | null | undefined) => (value == null ? '' : String(value));

// The Policies page's rule form (its titles and buttons included) on the editor types:
// Name, Description, 1. Condition, 2. Selection, 3. Limits, 4. Strategy. Local only — no
// requests; the dictionaries are the ones the widget warmed when edit mode started.
const RuleEditorForm: FC<IProps> = ({ rule, onSubmit }) => {
  const { closeModal } = useModal();
  const { data: settings } = useGetPolicySettingsQuery();
  const conditionFields = settings?.data?.conditions ?? [];
  const selectionFields = settings?.data?.selections ?? [];
  const strategies = settings?.data?.strategy ?? {};

  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [conditions, setConditions] = useState<TRuleParam[]>(() =>
    toRuleParamInputs(rule?.conditions ?? [], conditionFields),
  );
  const [selections, setSelections] = useState<TRuleParam[]>(() =>
    toRuleParamInputs(rule?.selections ?? [], selectionFields),
  );
  const [limits, setLimits] = useState<Record<TLimit, string>>({
    min: toLimitInput(rule?.min),
    max: toLimitInput(rule?.max),
  });
  // The rule's own strategy, else the dictionary's first; `random` if neither is known yet.
  const [strategy, setStrategy] = useState(
    rule?.sort_strategy || Object.keys(strategies)[0] || 'random',
  );
  const [errors, setErrors] = useState<TFieldErrors>({});
  const nameRef = useRef<HTMLInputElement>(null);
  const minRef = useRef<HTMLInputElement>(null);
  const maxRef = useRef<HTMLInputElement>(null);
  const fieldRefs = { name: nameRef, min: minRef, max: maxRef };

  const isEdit = rule !== undefined;

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const clearError = (field: TField) =>
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const setLimit = (key: TLimit, value: string) => {
    setLimits(prev => ({ ...prev, [key]: value }));
    clearError(key);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    const parsed = { min: toLimit(limits.min), max: toLimit(limits.max) };

    // One issue per field, in field order — the first one gets the focus.
    const issues = validateRule({ name: trimmed, ...parsed });
    if (issues.length) {
      setErrors(Object.fromEntries(issues.map(issue => [issue.path, issue.message])));
      fieldRefs[issues[0].path as TField].current?.focus();
      return;
    }

    onSubmit({
      name: trimmed,
      description: description.trim() || null,
      ...parsed,
      conditions: toRuleParams(conditions),
      selections: toRuleParams(selections),
      sort_strategy: strategy,
    });
    closeModal();
  };

  // Browser validation stays on (unlike the policy form): the count params' `min`
  // attributes block a submit below their minimum, as on the Policies page, and the
  // limits' range attributes block a submit outside 1..RULE_LIMIT_MAX the same way —
  // validateRule covers the rest (empty fields, max below min) inline.
  return (
    <form className='modal-sticky-layout routing-rule-form' onSubmit={handleSubmit}>
      <div className='modal-sticky-header'>
        <Typography variant='h6' fontWeight={700}>
          {isEdit ? 'Edit Rule' : 'Add Rule'}
        </Typography>
      </div>

      <div className='modal-sticky-content'>
        <Stack spacing={2}>
          <Field id='routing-rule-name' label='Name' error={errors.name}>
            <input
              id='routing-rule-name'
              ref={nameRef}
              type='text'
              placeholder='Rule Name'
              value={name}
              aria-invalid={errors.name !== undefined}
              onChange={event => {
                setName(event.target.value);
                clearError('name');
              }}
            />
          </Field>

          <FormItem name='routing-rule-description' label='Description'>
            <textarea
              id='routing-rule-description'
              placeholder='Description'
              value={description}
              onChange={event => setDescription(event.target.value)}
            />
          </FormItem>

          <RuleParamsSection
            title='1. Condition'
            items={conditions}
            fields={conditionFields}
            onChange={setConditions}
          />

          <RuleParamsSection
            title='2. Selection'
            items={selections}
            fields={selectionFields}
            onChange={setSelections}
          />

          <Stack spacing={2}>
            <Typography variant='subtitle2' fontWeight={600}>
              3. Limits
            </Typography>
            <Stack className='min-max' direction='row' spacing={2}>
              {LIMITS.map(({ key, label }) => (
                <Field key={key} id={`routing-rule-${key}`} label={label} error={errors[key]}>
                  <input
                    id={`routing-rule-${key}`}
                    ref={fieldRefs[key]}
                    type='number'
                    min={RULE_LIMIT_MIN}
                    max={RULE_LIMIT_MAX}
                    placeholder={label}
                    value={limits[key]}
                    aria-invalid={errors[key] !== undefined}
                    onChange={event => setLimit(key, event.target.value)}
                  />
                </Field>
              ))}
            </Stack>
          </Stack>

          <Stack spacing={2}>
            <Typography variant='subtitle2' fontWeight={600}>
              4. Strategy
            </Typography>
            <FormItem name='routing-rule-strategy' label='Strategy Type'>
              {/* A strategy the dictionary lacks (or an unloaded dictionary) stays selectable */}
              <select
                id='routing-rule-strategy'
                value={strategy}
                onChange={event => setStrategy(event.target.value)}
              >
                {toOptions(strategies, [strategy]).map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </FormItem>
          </Stack>
        </Stack>
      </div>

      <div className='modal-sticky-footer'>
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
          <button type='submit' className='primary-btn'>
            Save Rule
          </button>
        </Stack>
      </div>
    </form>
  );
};

export default RuleEditorForm;
